"use client";

// Runtime imports from React
import { useEffect, useState, createContext, useContext } from 'react';

// Type-only imports from React
import type { ReactNode } from 'react';

// Runtime imports from Firebase
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Type-only imports from Firebase
import type { FirebaseOptions } from 'firebase/app';

// Validate and create Firebase config with proper typing
const createFirebaseConfig = (): FirebaseOptions => {
  const config = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  };

  // Validate required Firebase configuration
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  
  for (const field of requiredFields) {
    if (!config[field as keyof typeof config]) {
      throw new Error(`Missing required Firebase configuration: NEXT_PUBLIC_FIREBASE_${field.toUpperCase()}`);
    }
  }

  return config as FirebaseOptions;
};

interface FirebaseContextType {
  isInitialized: boolean;
  error: string | null;
}

const FirebaseContext = createContext<FirebaseContextType>({
  isInitialized: false,
  error: null,
});

export function useFirebase() {
  return useContext(FirebaseContext);
}

interface FirebaseProviderProps {
  children: ReactNode;
}

export function FirebaseProvider({ children }: FirebaseProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize Firebase only once
    if (!getApps().length) {
      try {
        const firebaseConfig = createFirebaseConfig();
        const app = initializeApp(firebaseConfig);
        
        // Initialize services
        const auth = getAuth(app);
        const db = getFirestore(app);
        const storage = getStorage(app);
        
        // Analytics is NOT started here — it only starts after the visitor
        // accepts the cookie banner. See src/lib/analytics-consent.ts.

        // Connect to emulators in development
        if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === 'true') {
          try {
            connectAuthEmulator(auth, 'http://localhost:9099');
            connectFirestoreEmulator(db, 'localhost', 8080);
            connectStorageEmulator(storage, 'localhost', 9199);
            console.log('🔧 Connected to Firebase emulators');
          } catch (error) {
            console.warn('⚠️ Failed to connect to emulators:', error);
          }
        }
        
        console.log('✅ Firebase initialized successfully');
        setIsInitialized(true);
      } catch (error: any) {
        console.error('❌ Firebase initialization failed:', error);
        setError(error.message);
      }
    } else {
      setIsInitialized(true);
    }
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-center">
          <h2>Firebase Configuration Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <FirebaseContext.Provider value={{ isInitialized, error }}>
      {children}
    </FirebaseContext.Provider>
  );
}
